import { useMemo } from "react";
import _groupBy from "lodash/groupBy";
import _get from "lodash/get";

import { useGetThemeTree } from "./useGetThemeTree";
import { IThemeOption } from "@interfaces/sourcing.interface";
import { RelationObjectType } from "@enums/relations.enum";
import { useSourcingThemes } from "./useSourcingThemes";

export const useThemeOptions = () => {
  const { data: sourcingThemes = [] } = useSourcingThemes({ level: 0 });
  const fetchedThemeTreeResults = useGetThemeTree(sourcingThemes);

  const loadingThemeIds = useMemo(() => {
    const ids = new Set<string>();
    sourcingThemes.forEach((theme, index) => {
      if (fetchedThemeTreeResults[index]?.isFetching) {
        ids.add(theme.id);
      }
    });
    return ids;
  }, [fetchedThemeTreeResults, sourcingThemes]);

  const transformToThemeOptions = (themesToTransform: IThemeOption[]) => {
    return themesToTransform.map((theme) => ({
      id: theme.id,
      title: theme.title,
      level: theme.level,
      object_type: RelationObjectType.THEME,
      parent_list: theme?.parent_list || [],
      children: transformToThemeOptions(theme?.children || []),
    }));
  };

  const themeOptions: IThemeOption[] = useMemo(() => {
    const themeTreeResults = fetchedThemeTreeResults.flatMap((query) => query.data || []);
    const themeTreeLookup = _groupBy(themeTreeResults, "parent_id");

    return sourcingThemes.map((theme) => ({
      ...theme,
      children: transformToThemeOptions(themeTreeLookup[theme.id] || []),
      object_type: RelationObjectType.THEME,
      parent_list: [
        {
          id: theme.id,
          title: theme.title,
          level: theme.level,
        },
      ],
    }));
  }, [fetchedThemeTreeResults, sourcingThemes]);

  return { themeOptions, loadingThemeIds };
};
